import React, {
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { format } from 'date-fns';
import {
  ContextMenuComplete,
  ContextMenuItemType,
  OutlinedIcons,
  RichTextDisplay,
  ZSpinner,
} from '@marageti/z4-lib';
import { TravelerNote, TravelerNoteStatus } from '@marageti/z4-sdk/lib/people';

const { OutlinedPlusIcon } = OutlinedIcons;

const COLLAPSED_MAX_HEIGHT = 100;

const formatNoteDate = (iso: string) => format(new Date(iso), 'MMM d, yyyy');

function memberLabel(
  userId: string,
  nameById: Map<string, string>,
): string {
  if (userId === 'legacy') return 'Legacy';
  return nameById.get(userId) ?? userId;
}

type NoteBlockProps = {
  note: TravelerNote;
  nameById: Map<string, string>;
  isMemberNamesLoading: boolean;
  canEdit: boolean;
  onEdit: (note: TravelerNote) => void;
  onArchive: (note: TravelerNote) => void;
  onRestore: (note: TravelerNote) => void;
};

function NoteBlock({
  note,
  nameById,
  isMemberNamesLoading,
  canEdit,
  onEdit,
  onArchive,
  onRestore,
}: NoteBlockProps) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (contentRef.current) {
      setOverflows(contentRef.current.scrollHeight > COLLAPSED_MAX_HEIGHT);
    }
  }, [note.content]);

  const menuItems: ContextMenuItemType[] = canEdit ? [
    { label: 'Edit', action: () => onEdit(note) },
    note.status === TravelerNoteStatus.NOT_ARCHIVED
      ? { label: 'Archive', action: () => onArchive(note) }
      : { label: 'Restore', action: () => onRestore(note) },
  ] : [];

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} padding={1}>
      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          gap={1}
          sx={{ minWidth: 0, width: '100%' }}
        >
          <Typography
            variant="captionBold"
            component="p"
            color="text.primary"
            sx={{
              flex: '1 1 0',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {note.title}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.5} flexShrink={0}>
            <Typography variant="caption" color="text.primary" sx={{ whiteSpace: 'nowrap' }}>
              {formatNoteDate(note.updatedAt)}
              {' '}
              •
              {' '}
              {isMemberNamesLoading ? (
                <Skeleton
                  variant="text"
                  width={88}
                  height={18}
                  sx={{ display: 'inline-block', verticalAlign: 'text-bottom' }}
                />
              ) : (
                <Typography variant="captionItalic" color="text.primary" component="span">
                  {memberLabel(note.updatedBy, nameById)}
                </Typography>
              )}
            </Typography>
          </Stack>
        </Stack>
        <Stack spacing={0.5} sx={{ width: '100%' }}>
          <Box
            ref={contentRef}
            sx={{
              maxHeight: expanded ? 'none' : `${COLLAPSED_MAX_HEIGHT}px`,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <RichTextDisplay typography="caption" color="text.primary">
              {note.content}
            </RichTextDisplay>
          </Box>
          {note.content && overflows && (
            <Button
              size="small"
              variant="text"
              onClick={() => setExpanded((prev) => !prev)}
              sx={{
                alignSelf: 'flex-start',
                px: 0,
                minWidth: 0,
                typography: 'caption',
                color: 'text.primary',
              }}
            >
              {expanded ? 'Show Less' : 'Show More'}
            </Button>
          )}
        </Stack>
      </Stack>
      {menuItems.length > 0 && (
        <ContextMenuComplete id={`traveler-note-${note.id}`} items={menuItems} />
      )}
    </Stack>
  );
}

export type TravelerProfileNotesSectionViewProps = {
  isProfileLoading: boolean;
  activeNotes: TravelerNote[];
  archivedNotes: TravelerNote[];
  nameById: Map<string, string>;
  isMemberNamesLoading: boolean;
  isSaving: boolean;
  canEdit: boolean;
  onAddClick: () => void;
  onEdit: (note: TravelerNote) => void;
  onArchive: (note: TravelerNote) => void;
  onRestore: (note: TravelerNote) => void;
};

const TravelerProfileNotesSectionView = ({
  isProfileLoading,
  activeNotes,
  archivedNotes,
  nameById,
  isMemberNamesLoading,
  isSaving,
  canEdit,
  onAddClick,
  onEdit,
  onArchive,
  onRestore,
}: TravelerProfileNotesSectionViewProps) => {
  if (isProfileLoading) {
    return (
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: '12px',
          p: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 160,
        }}
      >
        <ZSpinner />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: '12px',
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
        <Typography variant="h3" component="h2">
          Traveler Notes
        </Typography>
        <IconButton
          onClick={onAddClick}
          aria-label="Add note"
          disabled={isSaving}
          sx={{ bgcolor: 'action.hover', '&:hover': { bgcolor: 'action.selected' } }}
        >
          <OutlinedPlusIcon color="primary" fontSize="small" />
        </IconButton>
      </Stack>

      {activeNotes.length === 0 ? (
        <Typography variant="body" color="text.disabled">
          {archivedNotes.length > 0 ? 'No active notes.' : 'No notes recorded.'}
        </Typography>
      ) : (
        <Stack spacing={2}>
          {activeNotes.map((n) => (
            <NoteBlock
              key={n.id}
              note={n}
              nameById={nameById}
              isMemberNamesLoading={isMemberNamesLoading}
              canEdit={canEdit}
              onEdit={onEdit}
              onArchive={onArchive}
              onRestore={onRestore}
            />
          ))}
        </Stack>
      )}

      {archivedNotes.length > 0 && (
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            bgcolor: 'transparent',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ px: 0, minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 } }}
          >
            <Typography variant="body" fontWeight={700}>
              {`Archived (${archivedNotes.length})`}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0 }}>
            <Stack spacing={2}>
              {archivedNotes.map((n) => (
                <NoteBlock
                  key={n.id}
                  note={n}
                  nameById={nameById}
                  isMemberNamesLoading={isMemberNamesLoading}
                  canEdit={canEdit}
                  onEdit={onEdit}
                  onArchive={onArchive}
                  onRestore={onRestore}
                />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default TravelerProfileNotesSectionView;
